import { GlassCard } from "./GlassCard";
import { Keyboard, Search, Repeat, Zap, ArrowDown, ArrowUp, Filter, UserPlus, LayoutDashboard, ArrowLeftRight, Scale } from "lucide-react";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.userAgent);
const ALT = isMac ? "⌥" : "Alt";
const MOD = isMac ? "⌘" : "Ctrl";

function Key({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-md bg-white/80 dark:bg-white/10 border border-stone-300/60 dark:border-white/15 text-[11px] font-mono font-semibold text-stone-700 dark:text-stone-100 shadow-sm">
      {children}
    </kbd>
  );
}

function Row({ icon: Icon, keys, label, desc }) {
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`shortcut-row-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-[color:var(--gold-soft)] flex items-center justify-center text-[color:var(--gold-deep)]">
        <Icon size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        {desc && <div className="text-xs text-stone-500 leading-snug mt-0.5">{desc}</div>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {keys.map((k, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-stone-400 text-xs">+</span>}
            <Key>{k}</Key>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ShortcutsPanel() {
  return (
    <GlassCard data-testid="shortcuts-panel">
      <div className="flex items-center gap-2 mb-1">
        <Keyboard size={18} className="text-[color:var(--gold-deep)]" />
        <h2 className="font-serif text-2xl">Shortcuts</h2>
        <span className="text-[10px] uppercase tracking-wider text-stone-500 ml-1">{isMac ? "macOS" : "Windows / Linux"}</span>
      </div>
      <p className="text-xs text-stone-500 mb-3">
        All shortcuts use <b>{ALT} + one letter</b> so they never collide with copy / cut / undo. All are left-hand only.
      </p>

      <div className="divide-y divide-stone-200/40">
        <Row
          icon={LayoutDashboard}
          keys={[ALT, "O"]}
          label="Overview"
          desc="Jump to the Dashboard / Overview page."
        />
        <Row
          icon={ArrowLeftRight}
          keys={[ALT, "E"]}
          label="Entries"
          desc="Jump to the Entries ledger."
        />
        <Row
          icon={Scale}
          keys={[ALT, "B"]}
          label="Balance Sheet"
          desc="Open the Balance Sheet page."
        />
        <Row
          icon={Search}
          keys={[ALT, "D"]}
          label="Search everything"
          desc={`Fuzzy-jump to any account or entry note. ${MOD}+K also works.`}
        />
        <Row
          icon={Filter}
          keys={[ALT, "S"]}
          label="Focus sidebar search"
          desc="Filter the account sidebar. Use ↑/↓ to walk the results, Enter to open the highlighted account, Esc to clear."
        />
        <Row
          icon={UserPlus}
          keys={[ALT, "N"]}
          label="Focus counterparty field"
          desc="Jump straight to the counterparty input on the new-entry row. Start typing to autocomplete from active accounts."
        />
        <Row
          icon={Repeat}
          keys={[ALT, "Space"]}
          label="Toggle 'Keep' counterparty"
          desc="When ON, the counterparty stays filled after each post — perfect for logging multiple entries for the same person."
        />
        <Row
          icon={Zap}
          keys={[ALT, "Z"]}
          label="Auto-fill zero-out amount"
          desc="Focuses the amount field. If a counterparty is already picked and this account has a non-zero balance, pre-fills the exact amount that zero-outs the account so a single Enter continues the flow."
        />
        <Row
          icon={ArrowDown}
          keys={[ALT, "X"]}
          label="Next account"
          desc="Move down the sidebar list."
        />
        <Row
          icon={ArrowUp}
          keys={[ALT, "C"]}
          label="Previous account"
          desc="Move up the sidebar list."
        />
      </div>

      <div className="mt-4 pt-3 border-t border-stone-200/40 grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-stone-500">
        <div><b>Enter</b> — Advance to next field, then Post.</div>
        <div><b>Esc</b> — Cancel any inline edit or close a dialog.</div>
        <div><b>Click a row</b> — Edit its date, amount or note inline.</div>
        <div><b>Double-click a row</b> — Mark it tallied (or untally).</div>
        <div><b>+50</b> in the amount field — Credit (money in).</div>
        <div><b>−50</b> in the amount field — Debit (money out).</div>
      </div>
    </GlassCard>
  );
}
